import { Button } from '@intigral/shared/components/Button';
import { AddonItem } from '@intigral/shared/components/AddonItem';
import { isUserInGracePeriod, redirectToPaymentSelectionPage } from '@intigral/utils';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import h from '../../../../helpers/common';
import { __ } from '../../../../translations';
import useToggleDetailOverlay from '../../../../hooks/useToggleDetailOverlay';
import useLanguage from '../../../../hooks/useLanguage';
import url from '../../../../helpers/url';
import useShowPPvCta from '../../../../hooks/useShowPPvCta';
import { PPVItemProps } from '../../types/iPPVCarousel';

export const PPVItem: React.FC<PPVItemProps> = ({ index, item, ppvEvents, userChannels, config }) => {
    const navigate = useNavigate();
    const language = useLanguage();
    const isGracePeriod = isUserInGracePeriod();

    const ppvEvent = ppvEvents.find((event) => event.subscriptionGuid === item.subscriptionGuid);
    const { showPpvOverlay } = useToggleDetailOverlay();
    const { isShowBuyNowCta, isAvailableSoonCta, isStreamingNow, isSubscribed, isRewind } = useShowPPvCta(ppvEvent);

    if (!ppvEvent) return null;

    const handleClickPlay = () => {
        const selectedChannel = userChannels?.channels.find((channel) =>
            channel?.subscriptions?.find((item) => item?.subscriptionGUID === ppvEvent?.subscriptionGuid)
        );
        const redirectUrl = url.route('livetv', {
            channelId: btoa(selectedChannel.Id || selectedChannel?.channelID),
        });
        navigate(redirectUrl);
    };

    const handleShowPpvOverlay = () => {
        showPpvOverlay({
            eventId: ppvEvent.eventId,
            type: 'Home_Screen',
        });
    };

    const handleUpdatePayment = () => {
        redirectToPaymentSelectionPage(config?.externalLinks?.myAccount, language);
    };

    const showPaymentFailed = isGracePeriod && isSubscribed;

    return (
        <AddonItem
            prefix={__('addon_text_for')}
            key={`ppv-${index}`}
            cost={!isSubscribed && `${ppvEvent.price}`}
            currency={`${ppvEvent.currency}`}
            logo={item.logo}
            picture={h.getImagizerUrl(item.mainImage, config?.imagizer?.components_images.VOD_ITEM)}
            status={
                showPaymentFailed
                    ? __('payment_failed_title')
                    : isStreamingNow
                      ? __('streaming_now')
                      : isSubscribed
                        ? __('subscribed')
                        : ''
            }
            statusColor={showPaymentFailed ? 'red' : isSubscribed ? 'green' : undefined}
            statusIconName={showPaymentFailed ? 'exclamation-mark' : isSubscribed ? 'check-green' : undefined}
            statusIconColor={showPaymentFailed ? 'red' : isSubscribed ? 'green' : undefined}
            ctaDetails={
                <Button compact plain onClick={handleShowPpvOverlay}>
                    {__('details')}
                </Button>
            }
            ctaPlay={
                <>
                    {isShowBuyNowCta && (
                        <Button compact onClick={handleShowPpvOverlay}>
                            {__('ppv_addon_buy_now')}
                        </Button>
                    )}
                    {!isRewind && isAvailableSoonCta && (
                        <Button
                            secondary={!isGracePeriod}
                            compact={isGracePeriod}
                            onClick={isGracePeriod ? handleUpdatePayment : handleShowPpvOverlay}
                        >
                            <span>{__(isGracePeriod ? 'update_payment_cta' : 'available_soon')}</span>{' '}
                        </Button>
                    )}
                    {isStreamingNow && isSubscribed && !isRewind && (
                        <Button
                            secondary={!isGracePeriod}
                            compact={isGracePeriod}
                            onClick={isGracePeriod ? handleUpdatePayment : handleClickPlay}
                        >
                            <span>{__(isGracePeriod ? 'update_payment_cta' : 'watch_now')}</span>{' '}
                        </Button>
                    )}
                    {isRewind && isSubscribed && !isStreamingNow && (
                        <Button
                            secondary={!isGracePeriod}
                            compact={isGracePeriod}
                            onClick={isGracePeriod ? handleUpdatePayment : handleClickPlay}
                        >
                            <span>{__(isGracePeriod ? 'update_payment_cta' : 'ppv_replay')}</span>{' '}
                        </Button>
                    )}
                </>
            }
            info={[
                moment(ppvEvent.eventBroadcastTs).format('MMMM D, YYYY'),
                moment(ppvEvent.eventBroadcastTs).format('h:mm A'),
            ]}
        />
    );
};
